/**
 * Prefetch the same route chunks `App.tsx` loads with `React.lazy`.
 * Specifiers must stay identical so Vite emits one chunk and the browser
 * module cache satisfies the later `lazy()` import before Suspense paints.
 */

type RouteLoader = () => Promise<unknown>;

const LOADERS: Record<string, RouteLoader> = {
  '/dashboard': () => import('@/modules/reports/pages/Dashboard'),
  '/patients': () => import('@/modules/patient/pages/Patients'),
  '/appointments': () => import('@/modules/appointment/pages/Appointments'),
  '/leads': () => import('@/modules/lead/pages/Leads'),
  '/messaging': () => import('@/modules/messaging/pages/InboxPage'),
  '/internal-chat': () => import('@/modules/internal-chat/pages/InboxPage'),
  '/payments': () => import('@/modules/payment/pages/Payments'),
  '/incomes': () => import('@/modules/income/pages/Incomes'),
  '/expenses': () => import('@/modules/expense/pages/Expenses'),
  '/cashbook': () => import('@/modules/finance/pages/Cashbook'),
  '/marketing/promotions': () => import('@/modules/promotion/pages/Promotions'),
  '/promotions': () => import('@/modules/promotion/pages/Promotions'),
  '/inventory/stock-in': () => import('@/modules/inventory/stock-in/pages/StockInList'),
  '/inventory/stock-out': () => import('@/modules/inventory/stock-out/pages/StockOutList'),
  '/inventory/transfers': () => import('@/modules/inventory/transfer/pages/TransferList'),
  '/inventory/stocktakes': () => import('@/modules/inventory/stocktake/pages/StocktakeList'),
  '/inventory/report': () => import('@/modules/inventory/reports/pages/InventoryReports'),
  '/inventory': () => import('@/modules/inventory/pages/WarehouseConfigHub'),
  '/treatment-catalog': () => import('@/modules/treatment-catalog/pages/TreatmentCatalog'),
  '/treatment-uoms': () => import('@/modules/treatment-catalog/pages/TreatmentUoms'),
  '/treatment-journals': () => import('@/modules/treatment-journal/pages/TreatmentJournals'),
  '/drugs': () => import('@/modules/prescription/pages/Drugs'),
  '/prescription-templates': () => import('@/modules/prescription/pages/PrescriptionTemplates'),
  '/appointment-types': () => import('@/modules/appointment/pages/AppointmentTypes'),
  '/customer-sources': () => import('@/modules/patient/pages/CustomerSources'),
  '/diagnosis-codes': () => import('@/modules/diagnosis/pages/DiagnosisCodes'),
  '/labo/master': () => import('@/modules/labo/pages/LaboMasterHub'),
  '/income-categories': () => import('@/modules/income/pages/IncomeCategories'),
  '/expense-categories': () => import('@/modules/expense/pages/ExpenseCategories'),
  '/payment-methods': () => import('@/modules/payment/pages/PaymentMethods'),
  '/projects': () => import('@/modules/project/pages/Projects'),
  '/tasks': () => import('@/modules/task/pages/Tasks'),
  '/staff': () => import('@/modules/staff/pages/Staff'),
  '/departments': () => import('@/modules/staff/pages/Departments'),
  '/attendance': () => import('@/modules/attendance/pages/AttendanceHub'),
  '/overtime': () => import('@/modules/overtime/pages/OvertimeHub'),
  '/work-timesheet': () => import('@/modules/work-timesheet/pages/WorkTimesheetPage'),
  '/kpi/cycles': () => import('@/modules/kpi/pages/KpiHub'),
  '/scheduling/time-off': () => import('@/modules/scheduling/time-off/pages/TimeOffHub'),
  '/scheduling': () => import('@/modules/scheduling/calendar/pages/TeamScheduleHub'),
  '/payroll': () => import('@/modules/payroll/pages/PayrollHub'),
  '/commissions': () => import('@/modules/commission/pages/CommissionHub'),
  '/treatment-plans': () => import('@/modules/treatment-plan/pages/TreatmentPlans'),
  '/treatment-sessions': () => import('@/modules/treatment-session/pages/TreatmentSessions'),
  '/prescriptions': () => import('@/modules/prescription/pages/Prescriptions'),
  '/labo/orders': () => import('@/modules/labo/orders/pages/LabOrders'),
  '/reports/revenue': () => import('@/modules/reports/pages/RevenueReport'),
  '/reports/patients': () => import('@/modules/reports/pages/PatientsReport'),
  '/marketing/referrers': () => import('@/modules/referrer/pages/ReferrerHub'),
  '/marketing/referral-commissions': () => import('@/modules/referrer/pages/ReferralCommissionHub'),
  '/reports/referrers': () => import('@/modules/reports/pages/ReferrersReport'),
  '/reports/appointments': () => import('@/modules/reports/pages/AppointmentsReport'),
  '/reports/treatments': () => import('@/modules/reports/pages/TreatmentsReport'),
  '/reports/leads': () => import('@/modules/lead/pages/LeadReports'),
  '/users': () => import('@/modules/identity/pages/Users'),
  '/permission-groups': () => import('@/modules/identity/pages/PermissionGroups'),
  '/audit': () => import('@/modules/audit/pages/AuditEvents'),
  '/automation': () => import('@/modules/automation/pages/Automation'),
  '/outbound': () => import('@/modules/settings/pages/OutboundChannels'),
  '/settings': () => import('@/modules/settings/pages/Settings'),
  '/profile': () => import('@/modules/identity/pages/Profile'),
  '/clinics': () => import('@/modules/clinic/pages/Clinics'),
  '/notifications': () => import('@/modules/notification/pages/Notifications'),
  '/platform/provision': () => import('@/modules/platform/pages/ProvisionOrganization'),
  '/platform': () => import('@/modules/platform/pages/PlatformOrganizations'),
};

const started = new Set<string>();

/**
 * Chunk for a menu href. Match is exact: a child URL such as `/patients/:id`
 * is a different lazy page, so it must not download the list chunk.
 * Query, hash, and a trailing slash are ignored.
 */
export function routeChunkId(to: string): string | null {
  const pathname = to.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  return Object.prototype.hasOwnProperty.call(LOADERS, pathname) ? pathname : null;
}

/** Start downloading the page chunk. Safe to call repeatedly. */
export function prefetchRoute(to: string): void {
  const id = routeChunkId(to);
  if (!id || started.has(id)) return;
  started.add(id);
  void LOADERS[id]().catch(() => {
    started.delete(id);
  });
}

export function prefetchRoutes(paths: readonly string[]): void {
  for (const path of paths) prefetchRoute(path);
}

/** Skip background warmup when the user asked the browser to save data. */
export function shouldBackgroundPrefetch(): boolean {
  const connection = (
    navigator as Navigator & { connection?: { saveData?: boolean } }
  ).connection;
  return !connection?.saveData;
}
